import { Component, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store, Select } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
    AlertService,
    ButtonColors,
    ButtonModule,
    NumberfieldModule,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { BusinessActivityModel, ProjectModel, ProjectStatus } from '../../project.models';
import { ProjectsState, UpdateProject } from '../../project.state';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { HeaderService } from 'src/app/shared/services/header.service';
import { Observable } from 'rxjs';
import { User } from 'src/app/core/models/user.model';
import { EDSearchComponent } from 'src/app/shared/components/ed-search/ed-search.component';
import {
    dateFormatValidator,
    EdDatepickerComponent,
} from 'src/app/shared/components/forms/ed-datepicker/ed-datepicker.component';

@UntilDestroy()
@Component({
    selector: 'project-update',
    templateUrl: './project-update.component.html',
    styleUrls: ['./project-update.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
        EDSearchComponent,
        EdDatepickerComponent,
    ],
})
export class ProjectUpdateComponent {
    public project!: ProjectModel;
    @Select(ProjectsState.projectWithDetail)
    public project$!: Observable<ProjectModel>;

    @Select(ProjectsState.activities)
    public activities$!: Observable<BusinessActivityModel[]>;
    public activityList: { value: string; label: string }[] = [];

    public statusList = Object.values(ProjectStatus).map((value) => ({ value, label: value }));
    public buttonColors = ButtonColors;

    @Select(ProjectsState.leadUsers)
    public leadUsers$!: Observable<User[]>;
    public leadUserOptions = signal<{ value: string; label: string }[]>([]);

    public projectFormGroup: FormGroup = this.formBuilder.group({
        code: [''],
        label: [''],
        soldAmount: [0],
        subcontractingAmount: [0],
        status: [ProjectStatus.ACTIVE],
        soldDayRate: [],
        estimatedCloseDate: [],
        activityId: [],
        leadUserId: [],
    });

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly store: Store,
        private readonly alertService: AlertService,
        private readonly sidenavService: SidenavService,
        private readonly headerService: HeaderService,
    ) {
        this.activities$
            .pipe(untilDestroyed(this))
            .subscribe((activities: BusinessActivityModel[]) => {
                this.activityList = activities.map((a) => ({ value: a.id, label: a.label }));
            });

        this.leadUsers$.pipe(untilDestroyed(this)).subscribe((leadUsers: User[]) => {
            this.leadUserOptions.set(
                leadUsers.map((a) => ({
                    value: a.id,
                    label: `${a.firstname} ${a.lastname}`,
                })),
            );
        });

        this.project$.pipe(untilDestroyed(this)).subscribe((project: ProjectModel) => {
            const code = this.route.snapshot.params['code'] || '';
            if (!project || !code) return;
            this.project = project;
            this.headerService.setBreadcrumbs([
                { link: '/main/projects/all', label: 'Projets' },
                {
                    label: `${project.code} - ${project.label}`,
                    link: `/main/projects/${project.code}`,
                },
                { label: 'Edition' },
            ]);
            this.sidenavService.resetSubMenuItem('Projets', [
                {
                    label: `Tous les projets`,
                    icon: 'bi bi-folder2-open',
                    routerLink: [`/main/projects`],
                    routerLinkActiveOptions: { exact: true },
                    hidden: false,
                    isActive: false,
                    subItems: [],
                },
                {
                    label: `${project.code} - ${project.label}`,
                    icon: 'bi bi-file-earmark-text',
                    routerLink: [`/main/projects/${project.code}/edit`],
                    routerLinkActiveOptions: { exact: true },
                    hidden: false,
                    isActive: false,
                    subItems: [],
                },
            ]);

            this.projectFormGroup.patchValue({
                code: this.project.code,
                label: this.project.label,
                soldAmount: this.project.soldAmount,
                subcontractingAmount: this.project.subcontractingAmount,
                status: this.project.status,
                soldDayRate: this.project.soldDayRate,
                estimatedCloseDate: this.project.estimatedCloseDate,
                activityId: this.project.activity?.id,
                leadUserId: this.project?.leadUser?.id,
            });
        });
    }

    public valid(): void {
        const project: any = {
            id: this.project.id,
            code: this.projectFormGroup.value.code,
            label: this.projectFormGroup.value.label,
            soldAmount: this.projectFormGroup.value.soldAmount,
            subcontractingAmount: this.projectFormGroup.value.subcontractingAmount,
            status: this.projectFormGroup.value.status,
            soldDayRate: this.projectFormGroup.value.soldDayRate,
            estimatedCloseDate: this.projectFormGroup.value.estimatedCloseDate,
            activityId: this.projectFormGroup.value.activityId,
            leadUserId: this.projectFormGroup.value.leadUserId,
        };

        //create
        this.store.dispatch(new UpdateProject({ project })).subscribe({
            next: () => {
                this.alertService.valid(
                    `Projet "${project.label}" mis à jour !`,
                    `Le projet à bien été mis à jour`,
                );

                let sum =
                    this.project.subcontractorInvoices?.reduce(
                        (acc, e) => acc + e.amountExclTax,
                        0,
                    ) || 0;
                if (sum !== project.subcontractingAmount) {
                    this.alertService.warning(
                        `Projet "${project.label}" mis à jour !`,
                        `Attention, le planning de sous traitance n'est plus à jour`,
                    );
                }
                sum =
                    this.project.billingMilestones?.reduce((acc, e) => acc + e.amountExclTax, 0) ||
                    0;
                if (sum !== project.soldAmount) {
                    this.alertService.warning(
                        `Projet "${project.label}" mis à jour !`,
                        `Attention, le planning de facturation n'est plus à jour`,
                    );
                }

                this.close();
            },
            error: (err: any) => {
                console.info(err.error.message);
                this.alertService.error('Mise à jour', err.error.message);
            },
        });
    }

    public close() {
        const code = this.projectFormGroup.value.code;
        this.router.navigate([`/main/projects/${code}/general`]);
    }

    public canCreateEdit(): boolean {
        return this.projectFormGroup.invalid || !this.projectFormGroup.dirty;
    }
}
